import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { login as apiLogin } from '@/lib'
import { useAuthStore } from '@/stores/auth'

interface UsePasswordLoginParams {
  redirectPath?: string
}

interface UsePasswordLoginReturn {
  email: string
  setEmail: (email: string) => void
  password: string
  setPassword: (password: string) => void
  persisted: boolean
  setPersisted: (persisted: boolean) => void
  emailValid: boolean
  setEmailValid: (valid: boolean) => void
  passwordValid: boolean
  setPasswordValid: (valid: boolean) => void
  formValid: boolean
  loading: boolean
  invalidPassword: boolean
  handleLoginClick: () => Promise<void>
}

/**
 * パスワードログインの状態とハンドラーを管理するカスタムフック
 * フォームバリデーション、ログイン処理、エラーハンドリングを担当
 */
export function usePasswordLogin({ redirectPath }: UsePasswordLoginParams): UsePasswordLoginReturn {
  const navigate = useNavigate()
  const { setUser } = useAuthStore()

  // Form state
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [persisted, setPersisted] = useState(true)

  // Validation state
  const [emailValid, setEmailValid] = useState(false)
  const [passwordValid, setPasswordValid] = useState(false)
  const [formValid, setFormValid] = useState(false)

  // UI state
  const [loading, setLoading] = useState(false)
  const [invalidPassword, setInvalidPassword] = useState(false)

  // Update form validation when email/password validation changes
  useEffect(() => {
    setFormValid(emailValid && passwordValid)
  }, [emailValid, passwordValid])

  /**
   * ログイン処理を実行し、成功時はリダイレクトする
   */
  const handleLoginClick = async () => {
    if (!formValid) return

    setInvalidPassword(false)
    setLoading(true)

    try {
      // ログインAPI呼び出し
      const userData = await apiLogin(email, password, persisted)

      // 認証ストアに保存
      setUser(userData)

      // リダイレクト先があればそこへ、なければルートへ
      // redirectPath は _authenticated.tsx が生成した URL を decode したもので末尾に ? が付く場合がある
      // TanStack Router の navigate は '?' のみの末尾を正しく解決できないため除去する
      const targetPath = (redirectPath || '/').replace(/\?$/, '') || '/'
      await navigate({ to: targetPath })
    } catch (error) {
      console.error('Login failed:', error)
      setInvalidPassword(true)
    } finally {
      setLoading(false)
    }
  }

  return {
    email,
    setEmail,
    password,
    setPassword,
    persisted,
    setPersisted,
    emailValid,
    setEmailValid,
    passwordValid,
    setPasswordValid,
    formValid,
    loading,
    invalidPassword,
    handleLoginClick,
  }
}
